/**
 * DayChart.jsx
 * One app's figures over the chosen window, day by day.
 *
 * Every day in the window is drawn, including the ones with nothing on them.
 * The old screen listed only days that had visits, which quietly turned a week
 * of silence into a continuous line of busy days sitting next to each other —
 * the one shape a run of zeroes must never be allowed to take.
 *
 * A day before this site was first counted is drawn differently again: not a
 * quiet day, but a day nobody was counting. That distinction is the whole
 * reason this screen was rebuilt.
 *
 * One series per chart, named by the heading above it, so colour carries no
 * meaning that the words do not already carry.
 *
 * Dependencies: utils/time.js, Bars.jsx
 */

import { daysBetween, formatDay } from '../../utils/time.js';
import { n } from './Bars.jsx';

/**
 * @param {object[]} rows [{ day, value }] — gaps are filled in here
 * @param {string} from first day of the window, "2026-08-23"
 * @param {string} to last day of the window
 * @param {string} [since] the first day this site was ever counted
 * @param {'visits'|'downloads'} [tone] which of the two hues to draw in
 * @param {string} unit the word for one of these, used in every tooltip
 */
export default function DayChart({ rows = [], from, to, since, tone = 'visits', unit = 'visits' }) {
  const byDay = new Map(rows.map((r) => [r.day, Number(r.value) || 0]));
  const series = daysBetween(from, to).map((day) => ({
    day,
    value: byDay.get(day) ?? 0,
    // Before the script was on the site, so a zero here means nothing.
    uncounted: !!since && day < since,
  }));

  if (series.length === 0) return <p className="row__sub">No days to show.</p>;

  const max = Math.max(1, ...series.map((d) => d.value));
  const total = series.reduce((sum, d) => sum + d.value, 0);
  const peak = series.find((d) => d.value === max && max > 0);

  return (
    <figure className={`daychart daychart--${tone}`}>
      <div
        className="daychart__plot"
        role="img"
        aria-label={`${n(total)} ${unit} between ${formatDay(series[0].day)} and ${formatDay(
          series[series.length - 1].day,
        )}, busiest day ${peak ? formatDay(peak.day) : 'none'} with ${n(max)}.`}
      >
        {series.map((d) => (
          <div
            className={`daychart__col ${d.uncounted ? 'is-uncounted' : ''}`}
            key={d.day}
            title={
              d.uncounted
                ? `${formatDay(d.day)} — not being counted yet`
                : `${formatDay(d.day)} — ${n(d.value)} ${d.value === 1 ? unit.replace(/s$/, '') : unit}`
            }
          >
            <div
              className="daychart__bar"
              style={{ height: `${Math.max(d.value ? 6 : 0, (d.value / max) * 100)}%` }}
            />
          </div>
        ))}
      </div>

      <figcaption className="daychart__axis">
        <span>{formatDay(series[0].day)}</span>
        <span className="daychart__peak">
          {peak ? `busiest ${formatDay(peak.day)} · ${n(max)}` : `nothing in these ${series.length} days`}
        </span>
        <span>{formatDay(series[series.length - 1].day)}</span>
      </figcaption>

      {/* The same figures as a list, for anyone who wants the numbers rather
          than the shape — and for a screen reader, which cannot see either. */}
      <details className="daychart__table">
        <summary>Show the {unit} day by day</summary>
        <table>
          <thead>
            <tr>
              <th>Day</th>
              <th>{unit[0].toUpperCase() + unit.slice(1)}</th>
            </tr>
          </thead>
          <tbody>
            {series.map((d) => (
              <tr key={d.day} className={d.uncounted ? 'is-uncounted' : ''}>
                <td>{formatDay(d.day)}</td>
                <td>{d.uncounted ? 'not counted' : n(d.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </figure>
  );
}
